import { FormControl, MenuItem, Pagination, Select, Typography } from "@mui/material";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { SCHOOLS_PATH } from "../../../functions/src/admin/paths";
import { formatUserActivityDateTime, formatWalletHistorySchoolYearLabel } from "../../../functions/src/api/dates";
import type { SchoolsList } from "../../../functions/src/types";
import { useFundHistoryForSchoolYear, useWalletHistoryYearSetup } from "../../api/funds";
import { getLangValue, langContext, type Language } from "../../api/Lang";
import { formatPriceEuro, formatSignedPriceEuro } from "../../api/priceFormat";
import { groupWalletHistoryRows, type WalletHistoryDisplayRow } from "../../api/walletHistory";
import { useStrings } from "../../common-strings";
import { useDatabase, useList } from "../../api/firebaseHooks";
import {
    getWalletHistoryBadgeLabel,
    getWalletHistoryBadgeVariant,
} from "./formatFundActivityLabel";
import { useWalletHistoryStrings, type WalletHistoryStrings } from "./wallet-history-strings";

const PAGE_SIZE = 20;

type WalletHistoryRowProps = {
    row: WalletHistoryDisplayRow;
    schoolLabel?: string;
    lang: Language;
    strings: WalletHistoryStrings;
};

function WalletHistoryRow({ row, schoolLabel, lang, strings }: WalletHistoryRowProps) {
    const badgeVariant = getWalletHistoryBadgeVariant(row.amount);
    const badgeLabel = getWalletHistoryBadgeLabel(row.amount, strings);

    return (
        <div className="wallet-history-row">
            <div className="wallet-history-row-top">
                <div className="wallet-history-row-top-start">
                    <span className={`wallet-history-badge wallet-history-badge--${badgeVariant}`}>
                        {badgeLabel}
                    </span>
                    {row.detail && <span className="wallet-history-detail">{row.detail}</span>}
                </div>
                <span className={`wallet-history-amount wallet-history-amount--${badgeVariant}`}>
                    {formatSignedPriceEuro(row.amount, lang)}
                </span>
            </div>
            <div className="wallet-history-row-children">
                <div className="wallet-history-row-meta">
                    <span>{formatUserActivityDateTime(row.timestamp, lang)}</span>
                    {schoolLabel && (
                        <>
                            <span className="wallet-history-meta-sep">·</span>
                            <span>{schoolLabel}</span>
                        </>
                    )}
                </div>
                <div className="wallet-history-balance">
                    {strings.balanceAfter}: {formatPriceEuro(row.balance, lang)}
                </div>
            </div>
        </div>
    );
}

export default function WalletHistory() {
    const [lang] = useContext(langContext);
    const strings = useWalletHistoryStrings();
    const commonStrings = useStrings();
    const { ready, schoolYearStartYear, setSchoolYearStartYear, schoolYearOptions } = useWalletHistoryYearSetup();
    const fundsSnapshot = useFundHistoryForSchoolYear(schoolYearStartYear);
    const fundsList = useList(fundsSnapshot);
    const allSchools = useDatabase(SCHOOLS_PATH);
    const [page, setPage] = useState(0);
    const listRef = useRef<HTMLDivElement>(null);

    const schoolNameByKey = useMemo(() => {
        const schools = (allSchools?.val() || {}) as SchoolsList;
        const map: Record<string, string> = {};
        for (const [key, school] of Object.entries(schools)) {
            if (school?.schoolName) map[key] = getLangValue(school.schoolName, lang) || key;
        }
        return map;
    }, [allSchools, lang]);

    const rows = useMemo(() => {
        if (!fundsList?.length) return [];
        const ledgerRows = fundsList.map((snap) => ({
            key: snap.key!,
            entry: snap.val(),
        }));
        return groupWalletHistoryRows(ledgerRows, strings, lang);
    }, [fundsList, strings, lang]);

    const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));

    useEffect(() => {
        setPage(0);
    }, [schoolYearStartYear, fundsSnapshot]);

    useEffect(() => {
        if (page >= totalPages) {
            setPage(Math.max(0, totalPages - 1));
        }
    }, [page, totalPages]);

    useEffect(() => {
        listRef.current?.scrollTo(0, 0);
    }, [page, schoolYearStartYear]);

    const pageRows = rows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
    const rangeFrom = rows.length === 0 ? 0 : page * PAGE_SIZE + 1;
    const rangeTo = Math.min((page + 1) * PAGE_SIZE, rows.length);

    if (!ready) {
        return (
            <div className="wallet-history-inline wallet-history-dialog-content">
                <div className="wallet-history-empty">{commonStrings.loading}</div>
            </div>
        );
    }

    return (
        <div className="wallet-history-inline wallet-history-dialog-content">
            <FormControl size="small" className="wallet-history-year-select" sx={{ minWidth: 140, mb: 2 }}>
                <Select
                    value={schoolYearStartYear!}
                    onChange={(e) => setSchoolYearStartYear(Number(e.target.value))}
                    displayEmpty
                    aria-label={strings.schoolYearLabel}
                >
                    {schoolYearOptions.map((startYear) => (
                        <MenuItem key={startYear} value={startYear}>
                            {formatWalletHistorySchoolYearLabel(startYear)}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {!fundsSnapshot && (
                <div className="wallet-history-empty">{commonStrings.loading}</div>
            )}
            {fundsSnapshot && rows.length === 0 && (
                <div className="wallet-history-empty">{strings.emptyForSchoolYear}</div>
            )}
            {pageRows.length > 0 && (
                <div ref={listRef} className="wallet-history-list">
                    {pageRows.map((row) => {
                        const schoolLabel = row.schoolKey ? schoolNameByKey[row.schoolKey] : undefined;

                        return (
                            <WalletHistoryRow
                                key={row.id}
                                row={row}
                                schoolLabel={schoolLabel}
                                lang={lang}
                                strings={strings}
                            />
                        );
                    })}
                </div>
            )}
            {rows.length > PAGE_SIZE && (
                <div className="wallet-history-pagination">
                    <Typography variant="caption" component="span" className="wallet-history-more">
                        {strings.pageSummary(rangeFrom, rangeTo, rows.length)}
                    </Typography>
                    <Pagination
                        count={totalPages}
                        page={page + 1}
                        onChange={(_, value) => setPage(value - 1)}
                        size="small"
                        color="primary"
                        sx={{
                            "& .MuiPaginationItem-root.Mui-selected": {
                                backgroundColor: "var(--lb_green)",
                            },
                        }}
                    />
                </div>
            )}
        </div>
    );
}
